import { useState, useEffect } from "react";
import {
  Button,
  Icon,
  Switch,
  useToast,
  Avatar,
  AvatarFallback,
  Heading,
} from "@elaniintc/goes-design-system";
import TextField, { TextAreaField } from "@/components/common/text-field";
import ScrollModal from "@/components/common/scroll-modal";
import { cn } from "@/utils/theme";
import {
  useCreateAnnouncement,
  useUpdateAnnouncement,
} from "@/queries/announcements/announcements.api";
import type { AnnouncementType } from "@/types/entities/announcements";

interface NewNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  notification?: AnnouncementType | null;
  onDelete?: (notification: AnnouncementType) => void;
}

const NewNotificationModal = ({
  isOpen,
  onClose,
  notification,
  onDelete,
}: NewNotificationModalProps) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);

  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const { showToast } = useToast();

  const isEditing = !!notification;
  const isArchived = notification?.status === "archived";

  // Effect to populate form when editing
  useEffect(() => {
    if (notification) {
      setTitle(notification.title);
      setContent(notification.content);
      setIsPublished(notification.status === "published");
    } else {
      setTitle("");
      setContent("");
      setIsPublished(false);
    }
  }, [notification]);

  // Función para obtener el texto del estatus
  const getStatusText = () => {
    if (isArchived) return "Archivada";
    return isPublished ? "Publicar" : "Sin publicar";
  };

  const handleSubmit = () => {
    const formData = {
      title: title.trim(),
      content: content.trim(),
      is_published: isPublished,
    };

    if (isEditing && notification) {
      updateMutation.mutate(
        {
          id: notification.id,
          ...formData,
        },
        {
          onSuccess: () => {
            showToast({
              title: "Cambios guardados",
              description:
                "Los cambios se guardaron exitosamente como borrador, puedes volver a publicarla.",
              options: {
                type: "success",
                duration: 4000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="action" shape="square">
                      <Icon type="fas" name="bell" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
            handleReset();
            onClose();
          },
          onError: () => {
            showToast({
              title: "Error al guardar",
              description:
                "Los cambios no pudieron ser guardados. Por favor vuelve a ingresar a la sección e inténtalo de nuevo.",
              options: {
                type: "error",
                duration: 4000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="danger" shape="square">
                      <Icon type="fas" name="bell-exclamation" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
          },
        },
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          showToast({
            title: "Nueva notificación creada",
            description:
              "La notificación se guardó correctamente. Puedes editarla o eliminarla en cualquier momento.",
            options: {
              type: "success",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="action" shape="square">
                    <Icon type="fas" name="bell-on" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
          handleReset();
          onClose();
        },
        onError: () => {
          showToast({
            title: "Error al crear notificación",
            description:
              "La notificación no pudo ser creada. Por favor inténtalo nuevamente.",
            options: {
              type: "error",
              duration: 4000,
              customIcon: (
                <Avatar size="xs" shape="square">
                  <AvatarFallback color="danger" shape="square">
                    <Icon type="fas" name="bell-exclamation" />
                  </AvatarFallback>
                </Avatar>
              ),
            },
          });
        },
      });
    }
  };

  const handleReset = () => {
    setTitle("");
    setContent("");
    setIsPublished(false);
  };

  const handleCancel = () => {
    handleReset();
    onClose();
  };

  const handleDelete = () => {
    if (notification && onDelete) {
      onDelete(notification);
      // No cerramos el modal aquí, se cerrará después de la confirmación
    }
  };

  // All required fields: título, contenido, and estatus (boolean always has value)
  const isFormValid =
    title.trim() !== "" && content.trim() !== "" && !isArchived;
  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <ScrollModal
      isOpen={isOpen}
      onClose={onClose}
      modalContentClassName="w-[520px] h-[680px] max-w-[520px] max-h-[680px] rounded-xl"
      header={
        <div className="p-6 pb-4">
          <div className="flex items-center justify-between w-full">
            <div className="text-center text-h2! font-semibold text-heading">
              <Heading variant="h5">
                {isEditing ? "Editar notificación" : "Nueva notificación"}
              </Heading>
            </div>
            <Button
              variant="quiet"
              size="xs"
              onClick={onClose}
              className="min-w-9 px-2 py-1.5 justify-center items-center gap-2 rounded-lg border border-neutral shadow-sm"
              data-testid="close-new-notification-modal-button"
            >
              <Icon type="far" name="times" className="text-sm text-body" />
            </Button>
          </div>
        </div>
      }
      footer={
        <div className="p-6 pt-4">
          {isEditing ? (
            // Layout para edición: 3 botones en la misma fila
            <div className="w-full flex items-center justify-between gap-3">
              {/* Botón Eliminar alineado a la izquierda */}
              <Button
                size="lg"
                type="button"
                className="rounded-lg"
                variant="danger-tertiary"
                onClick={handleDelete}
                disabled={isLoading}
              >
                Eliminar
              </Button>

              {/* Botones Cancelar y Guardar alineados a la derecha */}
              <div className="flex gap-3">
                <Button
                  size="lg"
                  type="button"
                  className="rounded-lg"
                  variant="tertiary"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={(e) => {
                    e.preventDefault(); // avoid form submission on status switch
                    handleSubmit();
                  }}
                  disabled={!isFormValid || isLoading}
                  size="lg"
                  className={cn(
                    "rounded-lg",
                    isFormValid && !isLoading
                      ? "bg-surface-action-solid text-on-solid hover:bg-surface-action-solid/90"
                      : "bg-neutral-object-accent text-on-disabled cursor-not-allowed",
                  )}
                >
                  {isLoading
                    ? "Actualizando..."
                    : isArchived
                      ? "Guardar cambios"
                      : "Actualizar notificación"}
                </Button>
              </div>
            </div>
          ) : (
            // Layout para creación: 2 botones
            <div className="w-full grid grid-cols-1 tablet:grid-cols-2 gap-3">
              <Button
                size="lg"
                type="button"
                className="w-full rounded-lg"
                variant="tertiary"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancelar
              </Button>
              <Button
                onClick={(e) => {
                  e.preventDefault(); // avoid form submission on status switch
                  handleSubmit();
                }}
                disabled={!isFormValid || isLoading}
                size="lg"
                className={cn(
                  "w-full rounded-lg",
                  isFormValid && !isLoading
                    ? "bg-surface-action-solid text-on-solid hover:bg-surface-action-solid/90"
                    : "bg-neutral-object-accent text-on-disabled cursor-not-allowed",
                )}
              >
                {isLoading ? "Creando..." : "Crear notificación"}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="p-6 pt-0">
        <form className="flex flex-col gap-6">
          <TextField
            label="Título"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Escribe el título de la notificación"
            className="text-body-loud"
            disabled={isArchived}
          />

          <TextAreaField
            label="Contenido"
            required
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Escribe el contenido de la notificación"
            className="relative h-47 max-h-47 text-body-loud"
            disabled={isArchived}
          />

          {/* Status Field */}
          <div className="flex flex-col items-start gap-1 w-full">
            <label className="flex items-center w-full font-semibold text-sm leading-6 text-body-loud">
              Estado
              <span className="font-semibold text-sm leading-6 text-danger ml-1">
                *
              </span>
            </label>
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2 w-full">
                <Switch
                  checked={isArchived ? true : isPublished}
                  required
                  onCheckedChange={setIsPublished}
                  id="notification-status-switch"
                  disabled={isArchived}
                />
                <label
                  htmlFor="notification-status-switch"
                  className="flex-1 text-base leading-7 font-normal cursor-pointer text-body"
                >
                  {getStatusText()}
                </label>
              </div>
              {isArchived && (
                <div className="text-body-quiet text-sm leading-5">
                  La notificación fue archivada automáticamente debido a que se
                  llegó al máximo permitido de notificaciones publicadas.
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </ScrollModal>
  );
};

export default NewNotificationModal;
